import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Database, Json } from '@/types/database.generated';
import { buildCreateJournalEntryRpcArgs } from '@/utils/journalPersist';

export type JournalEntryRow = Database['public']['Tables']['journal_entries']['Row'];
export type JournalEntryWithAuthor = JournalEntryRow & {
  author_name: string | null;
};
export type JournalLockRow = Database['public']['Tables']['journal_locks']['Row'];
export type JournalAuditRow = Database['public']['Tables']['journal_audit']['Row'];
export type JournalEntryType = Database['public']['Enums']['journal_entry_type'];
export type JournalAmendmentRow = Database['public']['Tables']['journal_entry_amendments']['Row'];

const JOURNAL_ENTRY_SELECT = '*, author:users!journal_entries_created_by_fkey(name,email)';

type JournalEntryJoined = JournalEntryRow & {
  author: { name: string | null; email: string } | null;
};

function mapJournalEntry(row: JournalEntryJoined): JournalEntryWithAuthor {
  const { author, ...entry } = row;
  // Prefer frozen snapshot so name changes do not rewrite historical notes.
  return {
    ...entry,
    author_name: entry.author_name_snapshot ?? author?.name ?? author?.email ?? null,
  };
}

export interface JournalLockState {
  acquired: boolean;
  holder_user_id: string;
  holder_user_name: string;
  expires_at: string;
}

// ---- Query keys ----

export const journalEntriesKey = (clinicId: string | null | undefined, patientId?: string) =>
  ['journal', 'entries', clinicId ?? null, patientId ?? null] as const;

export const journalEntryKey = (id: string | null | undefined) =>
  ['journal', 'entry', id ?? null] as const;

export const journalLocksKey = (clinicId: string | null | undefined) =>
  ['journal', 'locks', clinicId ?? null] as const;

export const journalAmendmentsKey = (entryId: string | null | undefined) =>
  ['journal', 'amendments', entryId ?? null] as const;

export function invalidateJournalCaches(
  qc: QueryClient,
  clinicId: string | null | undefined,
  patientId?: string | null,
  entryId?: string | null,
) {
  if (clinicId) {
    qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId) });
    if (patientId) {
      qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId, patientId) });
    }
    qc.invalidateQueries({ queryKey: journalLocksKey(clinicId) });
    qc.invalidateQueries({ queryKey: ['calendar', 'journals', clinicId] });
  }
  if (entryId) {
    qc.invalidateQueries({ queryKey: journalEntryKey(entryId) });
    qc.invalidateQueries({ queryKey: journalAmendmentsKey(entryId) });
  }
}

// ---- Queries ----

export function useJournalEntries(
  clinicId: string | null | undefined,
  opts: {
    patientId?: string;
    clinicianId?: string;
    from?: string;
    to?: string;
    includeDiscarded?: boolean;
    includeArchived?: boolean;
    limit?: number;
  } = {},
) {
  const includeHidden = Boolean(opts.includeDiscarded || opts.includeArchived);
  return useQuery({
    queryKey: [...journalEntriesKey(clinicId, opts.patientId), opts] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<JournalEntryWithAuthor[]> => {
      if (!clinicId) return [];
      let q = supabase
        .from('journal_entries')
        .select(JOURNAL_ENTRY_SELECT)
        .eq('clinic_id', clinicId)
        .order('entry_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(opts.limit ?? 200);

      if (opts.patientId) q = q.eq('patient_id', opts.patientId);
      if (opts.clinicianId) q = q.eq('clinician_id', opts.clinicianId);
      if (opts.from) q = q.gte('entry_date', opts.from);
      if (opts.to) q = q.lte('entry_date', opts.to);
      if (!includeHidden) {
        q = q.is('discarded_at', null);
      }

      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as unknown as JournalEntryJoined[]).map(mapJournalEntry);
    },
    staleTime: 10_000,
  });
}

export function useJournalEntry(id: string | null | undefined) {
  return useQuery({
    queryKey: journalEntryKey(id),
    enabled: Boolean(id),
    queryFn: async (): Promise<JournalEntryWithAuthor | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('journal_entries')
        .select(JOURNAL_ENTRY_SELECT)
        .eq('id', id)
        .single();
      if (error) throw error;
      return mapJournalEntry(data as unknown as JournalEntryJoined);
    },
    staleTime: 10_000,
  });
}

export const journalAmendmentCountsKey = (entryIds: string[]) =>
  ['journal', 'amendment-counts', ...[...entryIds].sort()] as const;

export function useJournalAmendmentCounts(entryIds: string[]) {
  const sortedIds = [...entryIds].sort();
  return useQuery({
    queryKey: journalAmendmentCountsKey(sortedIds),
    enabled: sortedIds.length > 0,
    queryFn: async (): Promise<Record<string, number>> => {
      if (sortedIds.length === 0) return {};
      const { data, error } = await supabase
        .from('journal_entry_amendments')
        .select('journal_entry_id, finalized_at')
        .in('journal_entry_id', sortedIds)
        .is('discarded_at', null)
        .not('finalized_at', 'is', null);
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of data ?? []) {
        counts[row.journal_entry_id] = (counts[row.journal_entry_id] ?? 0) + 1;
      }
      return counts;
    },
    staleTime: 10_000,
  });
}

export function useJournalAmendments(entryId: string | null | undefined) {
  return useQuery({
    queryKey: journalAmendmentsKey(entryId),
    enabled: Boolean(entryId),
    queryFn: async (): Promise<JournalAmendmentRow[]> => {
      if (!entryId) return [];
      const { data, error } = await supabase
        .from('journal_entry_amendments')
        .select('*')
        .eq('journal_entry_id', entryId)
        .is('discarded_at', null)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
}

// ---- Live lock cache via Realtime ----

export function useJournalLocksRealtime(clinicId: string | null | undefined) {
  const qc = useQueryClient();

  const locksQuery = useQuery({
    queryKey: journalLocksKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<JournalLockRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('journal_locks')
        .select('*')
        .eq('clinic_id', clinicId);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5_000,
  });

  useEffect(() => {
    if (!clinicId) return;

    const channel = supabase
      .channel(`journal_locks:clinic_id=eq.${clinicId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'journal_locks',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: journalLocksKey(clinicId) });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [clinicId, qc]);

  return locksQuery;
}

// ---- Mutations ----

export function useCreateJournalEntry(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      patient_id: string;
      entry_type?: JournalEntryType;
      title?: string;
      content_md?: string;
      content_structured?: Json;
      entry_date?: string;
      appointment_id?: string;
      template_id?: string;
      content_html?: string | null;
    }) => {
      const { data, error } = await supabase.rpc(
        'create_journal_entry',
        buildCreateJournalEntryRpcArgs({
          clinicId: clinicId!,
          patientId: input.patient_id,
          entryType: input.entry_type,
          title: input.title,
          contentMd: input.content_md,
          contentHtml: input.content_html,
          contentStructured: input.content_structured,
          entryDate: input.entry_date,
          appointmentId: input.appointment_id,
          templateId: input.template_id,
        }),
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (entry && clinicId) {
        invalidateJournalCaches(qc, clinicId, entry.patient_id, entry.id);
      }
    },
  });
}

export function useUpdateJournalEntry(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      content_md?: string;
      content_html?: string | null;
      entry_date?: string;
      row_version?: number;
    }) => {
      const { data, error } = await supabase.rpc('update_journal_entry', {
        p_id: input.id,
        p_title: input.title ?? undefined,
        p_content_md: input.content_md ?? undefined,
        p_content_html: input.content_html ?? undefined,
        p_entry_date: input.entry_date ?? undefined,
        p_expected_row_version: input.row_version ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (!entry || !clinicId) return;
      invalidateJournalCaches(qc, clinicId, entry.patient_id, entry.id);
    },
  });
}

export function useFinalizeJournalEntry(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: string | { id: string; row_version?: number }) => {
      const id = typeof input === 'string' ? input : input.id;
      const row_version = typeof input === 'string' ? undefined : input.row_version;
      const { data, error } = await supabase.rpc('finalize_journal_entry', {
        p_id: id,
        p_expected_row_version: row_version ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (!entry || !clinicId) return;
      invalidateJournalCaches(qc, clinicId, entry.patient_id, entry.id);
    },
  });
}

export function useDiscardJournalDraft(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('discard_journal_draft', { p_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (!entry || !clinicId) return;
      invalidateJournalCaches(qc, clinicId, entry.patient_id, entry.id);
    },
  });
}

/** @deprecated Draft discard only. Kept so older call sites keep compiling. */
export function useArchiveJournalEntry(clinicId: string | null | undefined) {
  return useDiscardJournalDraft(clinicId);
}

export function useRestoreJournalEntry(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('restore_journal_entry', { p_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (!entry || !clinicId) return;
      invalidateJournalCaches(qc, clinicId, entry.patient_id, entry.id);
    },
  });
}

export function useCreateJournalAmendment(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      journal_entry_id: string;
      content_md?: string;
      content_html?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('create_journal_amendment', {
        p_journal_entry_id: input.journal_entry_id,
        p_content_md: input.content_md ?? '',
        p_content_html: input.content_html ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      if (!row || !clinicId) return;
      invalidateJournalCaches(qc, clinicId, null, row.journal_entry_id);
    },
  });
}

export function useUpdateJournalAmendment(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      content_md: string;
      content_html?: string | null;
      row_version?: number;
    }) => {
      const { data, error } = await supabase.rpc('update_journal_amendment', {
        p_id: input.id,
        p_content_md: input.content_md,
        p_content_html: input.content_html ?? undefined,
        p_expected_row_version: input.row_version ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      if (!row || !clinicId) return;
      invalidateJournalCaches(qc, clinicId, null, row.journal_entry_id);
    },
  });
}

export function useFinalizeJournalAmendment(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; row_version?: number }) => {
      const { data, error } = await supabase.rpc('finalize_journal_amendment', {
        p_id: input.id,
        p_expected_row_version: input.row_version ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      if (!row || !clinicId) return;
      invalidateJournalCaches(qc, clinicId, null, row.journal_entry_id);
    },
  });
}

export function useDiscardJournalAmendment(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('discard_journal_amendment', { p_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      if (!row || !clinicId) return;
      invalidateJournalCaches(qc, clinicId, null, row.journal_entry_id);
      qc.invalidateQueries({ queryKey: ['journal', 'amendment-attachments', row.id] });
    },
  });
}

/** Canonical journal JSON payload. The Journal UI turns this into a PDF/ZIP download. */
export function useExportPatientJournal() {
  return useMutation({
    mutationFn: async (input: { patientId: string; exportMode: string }) => {
      const { data, error } = await supabase.rpc('export_patient_journal', {
        p_patient_id: input.patientId,
        p_export_mode: input.exportMode,
      });
      if (error) throw error;
      return data;
    },
  });
}

// ---- Lock mutations ----

export function useAcquireJournalLock(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string): Promise<JournalLockState> => {
      const { data, error } = await supabase.rpc('acquire_journal_lock', {
        p_entry_id: entryId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return row as JournalLockState;
    },
    onSettled: () => {
      if (clinicId) qc.invalidateQueries({ queryKey: journalLocksKey(clinicId) });
    },
  });
}

export function useReleaseJournalLock(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await supabase.rpc('release_journal_lock', { p_entry_id: entryId });
      if (error) throw error;
    },
    onSettled: () => {
      if (clinicId) qc.invalidateQueries({ queryKey: journalLocksKey(clinicId) });
    },
  });
}

export function useHeartbeatJournalLock() {
  return useMutation({
    mutationFn: async (entryId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('heartbeat_journal_lock', {
        p_entry_id: entryId,
      });
      if (error) throw error;
      return data as string;
    },
  });
}

/**
 * Sends a heartbeat every 60 seconds while the editor is mounted.
 * Returns `'lock_lost'` signal via onLockLost callback.
 */
export function useJournalHeartbeat(
  entryId: string | null | undefined,
  active: boolean,
  onLockLost: () => void,
) {
  const heartbeat = useHeartbeatJournalLock();
  const onLockLostRef = useRef(onLockLost);
  onLockLostRef.current = onLockLost;

  useEffect(() => {
    if (!entryId || !active) return;

    const interval = setInterval(async () => {
      try {
        const result = await heartbeat.mutateAsync(entryId);
        if (result === 'lock_lost') {
          onLockLostRef.current();
        }
      } catch {
        onLockLostRef.current();
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [entryId, active]); // eslint-disable-line react-hooks/exhaustive-deps
}
